import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaChevronUp, FaSearch } from "react-icons/fa";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import request from "../../utils/request";
import PageContainer from "../../components/PageContainer/PageContainer";
import DispatchPackageCard from "../../components/DispatchPackageCard/DispatchPackageCard";
import StorageSection from "../../components/StorageSection/StorageSection";
import PackageDetail from "../../components/PackageDetail/PackageDetail";
import styles from "./PackageStorage.module.css";
import t from "../../utils/translation";
import LocalizationContext from "../../utils/LocalizationContext";
import PackageLocationForm from "../PackageLocation/PackageLocationForm/PackageLocationForm";
import LocationLabel from "../PackageLocation/LocationLabel/LocationLabel";
import printLocation from "../../utils/printLocation";
import SpinnerQ from "../../components/SpinnerQ/SpinnerQ";
import BarcodeReader from "react-barcode-reader";
import { getSession } from "../../utils/sessionHandler";

export default function PackageStorage({ location }) {
  const locale = useContext(LocalizationContext);
  const [storages, setStorages] = useState([]);
  const [clickedPackages, setClickedPackages] = useState([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(
    { packageId: location?.state?.packageId } ?? null
  );
  const [leftSearch, setLeftSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStorage, setSelectedStorage] = useState("");
  const [destinationStorage, setDestinationStorage] = useState("");
  const [isMoving, setIsMoving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const initialValue = {
    id: null,
    locationName: "",
    code: "",
    observations: "",
  };
  const [formData, setFormData] = useState(initialValue);
  const [openStorages, setOpenStorages] = useState(true);

  useEffect(() => {
    getStorages();
  }, []);

  const getStorages = async () => {
    try {
      setLoading(true);
      const res = await request().get("/api/package/stored");

      const sortedData = res.data.sort((a, b) => {
        if (a.id === null && b.id === null) return 0;
        if (a.id === null) return -1;
        if (b.id === null) return 1;
        return a.id - b.id;
      });

      setStorages(sortedData);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error fetching storages");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredStorages = () => {
    const searchLower = searchTerm.toLowerCase();

    let storagesFiltered = storages
      .filter((st) => {
        if (!selectedStorage) return true;
        if (selectedStorage === "null") {
          return st.id === null;
        }
        return st.id === parseInt(selectedStorage, 10);
      })
      .map((st) => ({
        ...st,
        packages: st.packages.filter(
          (pkg) =>
            pkg.packageName.toLowerCase().includes(searchLower) ||
            String(pkg.packageNumber).includes(searchLower)
        ),
      }));

    if (searchTerm.trim() !== "") {
      storagesFiltered = storagesFiltered.filter(
        (st) => st.packages.length > 0
      );
    }

    return storagesFiltered;
  };

  const handleOpenAllStorages = () => {
    setOpenStorages((prev) => !prev);
  };

  const leftFilteredPackages = clickedPackages.filter(
    (p) =>
      p.packageName.toLowerCase().includes(leftSearch.toLowerCase()) ||
      String(p.packageNumber).includes(leftSearch.toLowerCase())
  );

  const handleLeftSearchChange = (e) => {
    setLeftSearch(e.target.value);
  };

  const handlePackageClick = (packageId) => {
    const allPackages = storages.flatMap((st) => st.packages);
    const pkg = allPackages.find((p) => p.packageId === packageId);
    if (!pkg) return;
    const alreadyClicked = clickedPackages.some(
      (cp) => cp.packageId === packageId
    );
    if (alreadyClicked) {
      setClickedPackages((prev) =>
        prev.filter((p) => p.packageId !== packageId)
      );
      setSelectedPackageIds((prev) => prev.filter((id) => id !== packageId));
    } else {
      setClickedPackages((prev) => [...prev, pkg]);
      setSelectedPackageIds((prev) => [...prev, packageId]);
    }
  };

  const onHandleScan = (data) => {
    let infoUser = getSession();
    let idDistributorCompany = infoUser.IdDistributorCompany;
    console.log(data);
    if (data.includes(`P${idDistributorCompany.replace(/-/g, "")}`)) {
      let scannedPackageNumber = data.replace(
        `P${idDistributorCompany.replace(/-/g, "")}`,
        ""
      );
      console.log(scannedPackageNumber);
      const allPackages = storages.flatMap((st) => st.packages);
      const pkg = allPackages.find(
        (p) => p.packageNumber === parseInt(scannedPackageNumber)
      );
      if (!pkg) {
        toast.error(t("BlankEntry195"));
        return;
      }
      const alreadyClicked = clickedPackages.some(
        (cp) => cp.packageId === pkg.packageId
      );
      if (alreadyClicked) {
        setClickedPackages((prev) =>
          prev.filter((item) => item.packageId !== pkg.packageId)
        );
        setSelectedPackageIds((prev) =>
          prev.filter((id) => id !== pkg.packageId)
        );
        toast.info(
          `${t("BlankEntry137")} ${pkg.packageNumber} ${t("BlankEntry135")}.`
        );
      } else {
        setClickedPackages((prev) => [...prev, pkg]);
        setSelectedPackageIds((prev) => [...prev, pkg.packageId]);
        toast.success(
          `${t("BlankEntry137")} ${pkg.packageNumber} ${t("BlankEntry136")}.`
        );
      }
    } else {
      let storage = storages.find(
        (s) => s.code.toLowerCase() === data.toLowerCase()
      );

      if (storage) {
        setDestinationStorage(storage.id);
      } else {
        toast.error("Scanned item not found");
      }
    }
  };

  const handleConfirmMove = async () => {
    if (!destinationStorage || clickedPackages.length === 0) {
      toast.error(t("BlankEntry138"));
      return;
    }
    setIsMoving(true);
    try {
      const body = {
        packageList: clickedPackages.map((pkg) => ({
          packageNumber: String(pkg.packageNumber),
        })),
        locationId: parseInt(destinationStorage, 10),
      };
      await request().post("/api/package/location-list", body);
      toast.success(`${t("BlankEntry132")}: ${clickedPackages.length}`);
      setClickedPackages([]);
      setSelectedPackageIds([]);
      setDestinationStorage("");
      getStorages();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error moving packages to location."
      );
    } finally {
      setIsMoving(false);
    }
  };

  const handleCancelMove = () => {
    setClickedPackages([]);
    setSelectedPackageIds([]);
    setDestinationStorage("");
    toast.info(t("BlankEntry133"));
  };

  const handleOpenAddModal = () => {
    setEditMode(false);
    setFormData(initialValue);
    setIsLocationModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsLocationModalOpen(false);
  };

  const handleFormSuccess = () => {
    getStorages();
    setIsLocationModalOpen(false);
  };

  const handleFormDataChange = (data) => {
    setFormData(data);
  };

  const handleDeleteStorage = (locationId) => {
    const storageObj = storages.find((st) => st.id === locationId);
    if (!storageObj) return;

    if (storageObj.packages && storageObj.packages.length > 0) {
      toast.error(t("BlankEntry131"));
      return;
    }

    const swal = withReactContent(Swal);
    swal
      .fire({
        title: t("DeleteConfirmationTitle"),
        text: t("DeleteConfirmationText"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: t("BlankEntry122"),
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            await request().delete(`/api/location?locationId=${locationId}`);
            toast.success(t("BlankEntry130"));
            getStorages();
          } catch (error) {
            toast.error(
              error?.response?.data?.message || "Error deleting location"
            );
          }
        }
      });
  };

  const handleEditStorage = (locationId) => {
    const storageObj = storages.find((st) => st.id === locationId);
    if (!storageObj) return;
    setEditMode(true);
    setFormData({
      id: storageObj.id,
      locationName: storageObj.locationName || "",
      code: storageObj.code || "",
      observations: storageObj.observations || "",
      colorValue: storageObj.colorValue,
    });
    setIsLocationModalOpen(true);
  };

  const handlePrintStorage = (locationId) => {
    const storageObj = storages.find((st) => st.id === locationId);
    if (!storageObj) return;
    const ReactSwal = withReactContent(Swal);
    ReactSwal.fire({
      html: <LocationLabel location={storageObj} />,
      background: "var(--grey)",
      showConfirmButton: true,
      confirmButtonText: t("Print"),
    }).then((result) => {
      if (result.isConfirmed) {
        printLocation(storageObj);
      }
    });
  };

  const filteredStorages = getFilteredStorages();

  return (
    <PageContainer
      categoryId={4}
      category={t("BlankEntry101") || "Storage"}
      backUrl={
        location?.state?.release ? "/appreleasecycle" : "/appdispatchmenu"
      }
      title={t("BlankEntry103") || "Package Stored"}
      subtitle={selectedPackage?.packageId ? t("DetailSpan") : null}
      clickBack={selectedPackage?.packageId ? true : null}
      onClickBack={() => setSelectedPackage({})}
    >
      {loading ? (
        <div className="spinnerContainer">
          <SpinnerQ category="dispatch" />
        </div>
      ) : !selectedPackage?.packageId ? (
        <div className={styles.containerLayout}>
          <BarcodeReader onScan={onHandleScan} />

          {/* Columna Izquierda */}
          <div className={`${styles.leftColumn} `}>
            <h2 className={styles.leftColumnTitle}>{t("BlankEntry104")}</h2>
            <div className={styles.leftSearchWrapper}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder={t("BlankEntry106")}
                value={leftSearch}
                onChange={handleLeftSearchChange}
                className={styles.leftSearchInput}
                disabled={isMoving}
              />
            </div>
            <div className={styles.leftPackagesScroll}>
              <AnimatePresence>
                {leftFilteredPackages.map((pkg) => (
                  <motion.div
                    key={pkg.packageId}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <DispatchPackageCard
                      pkg={pkg}
                      selected={selectedPackageIds.includes(pkg.packageId)}
                      onClick={() => handlePackageClick(pkg.packageId)}
                      onDetailClick={setSelectedPackage}
                      moveTo={3}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            <div className={styles.moveActions}>
              <label className={styles.destLabel}>{t("BlankEntry106")}</label>
              <select
                className={styles.destSelect}
                value={destinationStorage}
                onChange={(e) => setDestinationStorage(e.target.value)}
                disabled={isMoving}
              >
                <option value=""></option>
                {storages.map((st) => (
                  <option
                    key={st.id === null ? "nullOption" : st.id}
                    value={st.id === null ? "null" : st.id}
                  >
                    {`${st.locationName} ${st.id ? `(${st.code})` : ""}`}
                  </option>
                ))}
              </select>
              <button
                className={styles.confirmButton}
                onClick={handleConfirmMove}
                disabled={isMoving}
              >
                {isMoving ? `${t("BlankEntry110")}...` : t("ConfirmButton")}
              </button>
              <button
                className={styles.cancelButton}
                onClick={handleCancelMove}
                disabled={isMoving}
              >
                {t("Cancel")}
              </button>
            </div>
          </div>

          {/* Contenido Principal */}
          <div className={styles.mainContent}>
            <div className={styles.topBar}>
              <div className={styles.searchWrapper}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder={`${t("BlankEntry128")}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                  disabled={isMoving}
                />
              </div>

              <select
                className={styles.storageFilter}
                value={selectedStorage}
                onChange={(e) => setSelectedStorage(e.target.value)}
                disabled={isMoving}
              >
                <option value="">{t("BlankEntry107")}</option>
                {storages.map((st) => (
                  <option
                    key={st.id === null ? "nullKey" : st.id}
                    value={st.id === null ? "null" : st.id}
                  >
                    {st.locationName}
                  </option>
                ))}
              </select>

              <button
                className={styles.moveButton}
                onClick={handleOpenAddModal}
                disabled={isMoving}
              >
                {t("BlankEntry109")}
              </button>
              <button
                className={styles.moveButton}
                onClick={handleOpenAllStorages}
                disabled={isMoving}
              >
                {openStorages ? (
                  <>
                    <FaChevronUp style={{ marginRight: 6 }} />
                    {t("BlankEntry181")}
                  </>
                ) : (
                  <>
                    <FaChevronDown style={{ marginRight: 6 }} />
                    {t("BlankEntry182")}
                  </>
                )}
              </button>
            </div>

            {/* <div className={styles.moveHint}>
                <p>{t("BlankEntry112")}</p>
              </div> */}

            {filteredStorages.map((storage) => (
              <StorageSection
                key={storage.id === null ? "nullStorage" : storage.id}
                storageId={storage.id}
                storageName={`${storage.locationName} ${
                  storage.id ? `(${storage.code})` : ""
                }`}
                pkgs={storage.packages}
                selectedPackageIds={selectedPackageIds}
                handlePackageClick={handlePackageClick}
                setSelectedPackage={setSelectedPackage}
                actions={storage.id !== null}
                onEdit={handleEditStorage}
                onDelete={handleDeleteStorage}
                onPrint={handlePrintStorage}
                toggle={openStorages}
                storageColor={storage.colorValue}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.detailContainer}>
          <PackageDetail
            packageId={selectedPackage.packageId}
            locale={locale}
            storage={true}
          />
        </div>
      )}
      {isLocationModalOpen && (
        <PackageLocationForm
          open={isLocationModalOpen}
          handleClose={handleCloseModal}
          formData={formData}
          handleFormData={handleFormDataChange}
          getPackageLocation={handleFormSuccess}
          t={t}
          locale={locale}
          editMode={editMode}
        />
      )}
    </PageContainer>
  );
}
